import { useContext, useEffect, useState } from 'react'
import firebase from '../../../firebase/compat'
import { db } from '../../../firebase/config'
import { client } from '../../../hooks/Client'
import { Auth } from '../../../StateManagment/Auth'
import { SavedIcon } from '../../../StateManagment/SavedIcon'
import Modal from '../../common/Modal'
import buildProjectTask from '../../../helpers/portfolio/buildProjectTask'

/**
 * Zet een Firestore-Timestamp om in de waarde van een `<input type="date">`.
 *
 * @param {{toDate?: () => Date}|null|undefined} value
 * @returns {string} 'YYYY-MM-DD', of '' als er geen tijdstip is
 */
const toDateInput = (value) => {
    if (!value?.toDate) return ''
    const date = value.toDate()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${date.getFullYear()}-${month}-${day}`
}

/**
 * Zet een Firestore-Timestamp om in de waarde van een `<input type="time">`.
 *
 * @param {{toDate?: () => Date}|null|undefined} value
 * @returns {string} 'HH:MM', of '' als er geen tijdstip is
 */
const toTimeInput = (value) => {
    if (!value?.toDate) return ''
    const date = value.toDate()
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/**
 * Licht invulvenster voor een projectafspraak. Bewust niet de gedeelde
 * TaskEditor: die gaat over huisbezoeken, roosters en subtaken.
 *
 * AI-CONTEXT: See docs/features/deccos-projectafspraken.md
 *
 * @typedef {object} TeamUser
 * @property {string} ID
 * @property {string} [UserName]
 * @property {string} [Email]
 *
 * @param {object} props
 * @param {boolean} props.modalOpen
 * @param {(open: boolean) => void} props.setModalOpen
 * @param {string} [props.projectId] - CompagnyID van het klantproject
 * @param {string} [props.projectName] - Naam van het project, gaat mee op de afspraak voor de agenda
 * @param {object|null} [props.task] - Bestaande afspraak; null betekent nieuw
 * @param {number} [props.position] - Volgnummer voor een nieuwe afspraak
 * @param {number} [props.openCount] - Telt hoe vaak het venster geopend is; dwingt het
 *   formulier terug naar de meegegeven afspraak, ook twee keer achter elkaar 'nieuw'
 * @param {Array<TeamUser>} [props.users] - Collega's die toegewezen kunnen worden
 */
const ProjectTaskModal = ({ modalOpen, setModalOpen, projectId, projectName = '', task = null, position = 0, openCount = 0, users = [] }) => {
    // Context
    const [auth] = useContext(Auth)
    const [, setSaved] = useContext(SavedIcon)

    // State
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [date, setDate] = useState('')
    const [startTime, setStartTime] = useState('')
    const [endTime, setEndTime] = useState('')
    const [allDay, setAllDay] = useState(false)
    const [teamMembers, setTeamMembers] = useState(/** @type {Array<string>} */ ([]))
    const [errors, setErrors] = useState(/** @type {Array<string>} */ ([]))

    // Het formulier volgt de afspraak die geopend wordt. Niet het task-object
    // als afhankelijkheid: een nieuwe snapshot van dezelfde afspraak mag het
    // concept niet wissen terwijl iemand zit te typen.
    // `openCount` moet erbij: twee keer achter elkaar 'toevoegen' laat `task?.ID`
    // op undefined staan, en dan zou het venster het vorige concept nog tonen.
    // AI-CONTEXT: See docs/pitfalls.md#state-draft-wiped-by-firestore-snapshot
    useEffect(() => {
        setTitle(task?.Title || '')
        setDescription(task?.Description || '')
        setDate(toDateInput(task?.Timestamp))
        setStartTime(toTimeInput(task?.Timestamp))
        setEndTime(toTimeInput(task?.TimestampEnd))
        setAllDay(Boolean(task) && !task?.TimestampEnd)
        setTeamMembers(Array.isArray(task?.TeamMembers) ? task.TeamMembers : [])
        setErrors([])
        // Bewust alleen op de ID: het task-object zelf is een Firestore-snapshot
        // en zou het concept bij elke update wissen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [task?.ID, openCount])

    const toggleMember = (userId) => {
        setTeamMembers((previous) => previous.includes(userId)
            ? previous.filter((id) => id !== userId)
            : [...previous, userId])
    }

    const save = async () => {
        const { errors: found, task: built } = buildProjectTask({
            projectId,
            projectName,
            compagnyId: client,
            userId: auth?.ID || '',
            title,
            description,
            date,
            startTime,
            endTime,
            allDay,
            teamMembers,
            position,
            toTimestamp: (value) => firebase.firestore.Timestamp.fromDate(value),
        })

        if (found.length > 0) {
            setErrors(found)
            return
        }

        if (task?.docid) {
            // Bijwerken raakt alleen wat het formulier bestrijkt; ID, auteur en
            // volgnummer blijven van de bestaande afspraak.
            await db.collection('Tasks').doc(task.docid).update({
                Title: built.Title,
                Description: built.Description,
                Timestamp: built.Timestamp,
                TimestampEnd: built.TimestampEnd,
                TeamMembers: built.TeamMembers,
                ProjectName: built.ProjectName,
            })
        } else {
            await db.collection('Tasks').add(built)
        }

        setErrors([])
        setModalOpen(false)
        setSaved('flex')
    }

    return (
        <Modal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            pageHeader={task ? 'Afspraak bewerken' : 'Afspraak toevoegen'}
            confirm={save}
            confirmLabel={'Opslaan'}
            content={
                <div className="project-afspraken-form">
                    <label className="project-afspraken-field">
                        <span>Titel</span>
                        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
                    </label>
                    <label className="project-afspraken-field">
                        <span>Omschrijving</span>
                        <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
                    </label>
                    <label className="project-afspraken-field">
                        <span>Datum <span className="project-afspraken-optional">(optioneel)</span></span>
                        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                    </label>
                    <label className="project-afspraken-checkbox">
                        <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
                        <span>Hele dag</span>
                    </label>
                    {!allDay && (
                        <div className="project-afspraken-times">
                            <label className="project-afspraken-field">
                                <span>Starttijd</span>
                                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                            </label>
                            <label className="project-afspraken-field">
                                <span>Eindtijd</span>
                                <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                            </label>
                        </div>
                    )}
                    <fieldset className="project-afspraken-members">
                        <legend>Toegewezen aan</legend>
                        {users.map((user) => (
                            <label className="project-afspraken-checkbox" key={user.ID}>
                                <input
                                    type="checkbox"
                                    checked={teamMembers.includes(user.ID)}
                                    onChange={() => toggleMember(user.ID)}
                                />
                                <span>{user.UserName || user.Email || user.ID}</span>
                            </label>
                        ))}
                    </fieldset>
                    {errors.length > 0 && (
                        <ul className="project-afspraken-error" role="alert">
                            {errors.map((message) => <li key={message}>{message}</li>)}
                        </ul>
                    )}
                </div>
            }
        />
    )
}

export default ProjectTaskModal
